import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import { Package, Calendar, AlertCircle, Truck, CheckCircle } from "lucide-react";

const SellerOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shippingOrderId, setShippingOrderId] = useState(null);

    useEffect(() => {
        fetchSellerOrders();
    }, []);

    const fetchSellerOrders = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/orders/seller/orders`, {
                headers: {
                    Authorization: token,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch seller orders");
            }

            const data = await response.json();
            // Sort by newest first
            const sortedOrders = data.sort(
                (a, b) => new Date(b.orderDate) - new Date(a.orderDate)
            );
            setOrders(sortedOrders);
            setLoading(false);
        } catch (err) {
            setError("ไม่สามารถโหลดข้อมูลคำสั่งซื้อได้");
            setLoading(false);
        }
    };

    const handleShipOrder = async (orderId) => {
        if (!window.confirm("ยืนยันการจัดส่งสินค้า?")) return;

        setShippingOrderId(orderId);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/ship`, {
                method: "PUT",
                headers: { Authorization: token },
            });

            if (response.ok) {
                alert("อัปเดตสถานะจัดส่งสำเร็จ");
                fetchSellerOrders();
            } else {
                const data = await response.json();
                alert(data.error || "เกิดข้อผิดพลาด");
            }
        } catch (err) {
            alert("เกิดข้อผิดพลาดในการจัดส่ง");
        } finally {
            setShippingOrderId(null);
        }
    };

    const getShippingBadge = (status) => {
        if (status === "SHIPPED") {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle size={12} />
                    ซื้อสำเร็จ
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                <Package size={12} />
                รอจัดส่ง
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center text-red-700">
                <AlertCircle className="h-5 w-5 mr-2" />
                {error}
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center">
                <Package className="h-5 w-5 mr-3 text-primary" />
                <h2 className="text-lg font-bold text-primary">รายการคำสั่งซื้อ</h2>
            </div>

            {orders.length === 0 ? (
                <div className="text-center py-12 text-secondary">
                    <p>ยังไม่มีคำสั่งซื้อ</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                                    รหัสคำสั่งซื้อ
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                                    สินค้า
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                                    ผู้ซื้อ
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                                    วันที่
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                                    ยอดรวม
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                                    สถานะจัดส่ง
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-secondary uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {orders.map((order) => (
                                <tr key={order.orderId} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary">
                                        #{order.orderId}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10 border border-gray-200 rounded-full overflow-hidden">
                                                <img
                                                    className="h-full w-full object-cover"
                                                    src={order.productImage ? `${API_BASE_URL}${order.productImage}` : "https://via.placeholder.com/40?text=N/A"}
                                                    alt=""
                                                    onError={(e) => { e.target.src = "https://via.placeholder.com/40?text=N/A"; }}
                                                />
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-primary">{order.productName}</div>
                                                <div className="text-xs text-secondary">จำนวน: {order.quantity}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary">
                                        User #{order.buyerId}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary">
                                        <div className="flex items-center">
                                            <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                                            {new Date(order.orderDate).toLocaleDateString("th-TH")}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary font-medium">
                                        ฿{order.totalAmount?.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {getShippingBadge(order.shippingStatus)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        {order.shippingStatus !== "SHIPPED" && (
                                            <button
                                                onClick={() => handleShipOrder(order.orderId)}
                                                disabled={shippingOrderId === order.orderId}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                            >
                                                <Truck size={14} />
                                                {shippingOrderId === order.orderId ? "กำลังดำเนินการ..." : "จัดส่งแล้ว"}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SellerOrders;

